import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FolderOpen, Trash2, Plus } from 'lucide-react';
import { privateAlbumService } from '@/services/privateAlbumService';
import { toast } from '@/hooks/use-toast';
import PrivateAlbumViewer from './PrivateAlbumViewer';

interface PrivateAlbumsListProps {
  albums: any[];
  refreshKey: number;
  onAlbumDeleted: () => void;
  limits: any;
}

const PrivateAlbumsList = ({ 
  albums, 
  refreshKey, 
  onAlbumDeleted, 
  limits 
}: PrivateAlbumsListProps) => {
  const [selectedAlbum, setSelectedAlbum] = useState<any>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDeleteAlbum = async (albumId: string, albumName: string) => {
    if (!confirm(`Are you sure you want to delete "${albumName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(albumId);
      await privateAlbumService.deleteAlbum(albumId);
      
      toast({
        title: "Album Deleted",
        description: `"${albumName}" has been deleted.`
      });
      
      onAlbumDeleted();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete album",
        variant: "destructive"
      });
    } finally {
      setDeleting(null);
    }
  };

  if (albums.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No private albums yet</p>
        <p className="text-sm">Create your first album to get started</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {albums.map((album) => (
          <Card key={album.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="font-medium">{album.name}</h4>
                  <p className="text-sm text-muted-foreground">
                    Created {new Date(album.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedAlbum(album)}
                  >
                    <FolderOpen className="w-4 h-4 mr-1" />
                    Open
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteAlbum(album.id, album.name)}
                    disabled={deleting === album.id}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedAlbum && (
        <PrivateAlbumViewer
          album={selectedAlbum}
          open={!!selectedAlbum}
          onOpenChange={() => setSelectedAlbum(null)}
          limits={limits}
          onMediaUpdated={onAlbumDeleted}
        />
      )}
    </>
  );
};

export default PrivateAlbumsList;